import React, { useCallback, useEffect, useState } from 'react'
import api from '../../../../../services/api'
import Form, {
  Input,
  Select,
  SelectSearch
} from '../../../../../components/Form'
import { useHistory } from 'react-router-dom'
import { useToast } from '../../../../../hooks/toast'
import { useLoading } from '../../../../../hooks/loading'
import { useUpdateDataTable } from '../../../../../hooks/dataTable'
import { apiCreate, apiUpdate } from '../../domain/api'
import { AGENCY_PARTNERSHIP_TYPE_MAP, nameActions } from '../../domain/info'
import {
  AGENCY_USER_ROLE_ENUM,
  AGENCY_USER_ROLE_MAP
} from '../../../AgenciesUsers/domain/info'
import { FormContainer } from './styles'
import { INPUT_MASK } from '../../../../../common/constants'
import axios from 'axios'
import { SELECT_BRAZILIAN_STATES } from '../../../../../common/constants/states-brazilian'
import { apiListPromotersOptions } from '../../../Promoters/domain/api'
import { v4 } from 'uuid'
import { apiListOptionsByPromoter } from '../../../Routes/domain/api'

type IsOpenInModalProps = {
  idParent: number
  handleOnClose: () => void
}

type AgencyData = {
  id?: string
  promoter_id: number
  name: string
  responsible?: string
  cnpj: string
  street: string
  number?: string
  complement?: string
  district: string
  city: string
  uf: string
  zip_code: string
  country: string
  phone_number?: string | null
  whatsapp_number?: string | null
  latitude?: string | null
  longitude?: string | null
  location_link_maps?: string | null
  partnership_type: string
  routes?: {
    route_id: number
  }[]
}

type AgencyResponseData = {
  agency: {
    id?: string
    promoter_id: number
    name: string
    responsible?: string
    cnpj: string
    street: string
    number?: string
    complement?: string
    district: string
    city: string
    uf: string
    zip_code: string
    country: string
    phone_number?: string | null
    whatsapp_number?: string | null
    latitude?: string | null
    longitude?: string | null
    location_link_maps?: string | null
    partnership_type: string
  }
  agencyUsers?: {
    id: number | string
    show?: boolean
    name: string
    email: string
    password?: string
    role: AGENCY_USER_ROLE_ENUM
  }[]
  route?: {
    id: number
  }
}

type TypesFormProps = {
  isOpenInModal?: false | IsOpenInModalProps
  initialValues?: AgencyData & {
    idUpdate: number
  }
  typeForm: 'create' | 'update'
}

export const FormAgency = ({
  isOpenInModal,
  initialValues,
  typeForm
}: TypesFormProps): JSX.Element => {
  const { addToast } = useToast()
  const history = useHistory()
  const { updateDataTable } = useUpdateDataTable()

  const [zipCodeValue, setZipCodeValue] = useState('')
  const [cityValue, setCityValue] = useState('')
  const [districtValue, setDistrictValue] = useState('')
  const [streetValue, setStreetValue] = useState('')
  const [numberValue, setNumberValue] = useState('')
  const [stateValue, setStateValue] = useState('')
  const [agencyUsers, setAgencyUsers] = useState<
    AgencyResponseData['agencyUsers']
  >([])

  const [promotersOptions, setPromotersOptions] = useState([])
  const [routesOptions, setRoutesOptions] = useState([])

  const [defaultValues, setDefaultValues] = useState<AgencyResponseData>()

  const fetchRoutesOptions = useCallback(async (promoter_id: string) => {
    try {
      const responseRoutes = await api.get(
        apiListOptionsByPromoter(promoter_id)
      )
      setRoutesOptions(
        responseRoutes.data?.map(
          (item: { id: number; name: string; scheduled_date: string }) => ({
            name: `${item.name} - ${item.scheduled_date
              ?.split('-')
              .reverse()
              .join('/')}`,
            value: item.id
          })
        )
      )
    } catch (error) {}
  }, [])

  useEffect(() => {
    if (initialValues) {
      if (initialValues.promoter_id) {
        fetchRoutesOptions(String(initialValues.promoter_id))
      }

      setDefaultValues({
        agency: {
          promoter_id: initialValues.promoter_id,
          name: initialValues.name,
          responsible: initialValues.responsible,
          cnpj: initialValues.cnpj,
          street: initialValues.street,
          number: initialValues.number,
          complement: initialValues.complement,
          district: initialValues.district,
          city: initialValues.city,
          uf: initialValues.uf,
          zip_code: initialValues.zip_code,
          country: initialValues.country,
          phone_number: initialValues.phone_number,
          whatsapp_number: initialValues.whatsapp_number,
          latitude: initialValues.latitude,
          longitude: initialValues.longitude,
          location_link_maps: initialValues.location_link_maps,
          partnership_type: initialValues.partnership_type
        },
        route: {
          id: initialValues.routes?.[0]?.route_id
        }
      })
    }
  }, [initialValues, typeForm, fetchRoutesOptions])

  const { activeLoading, disableLoading } = useLoading()

  const onSubmitForm = async (data: AgencyResponseData) => {
    const id = initialValues?.idUpdate

    data.agencyUsers = data.agencyUsers?.filter(Boolean)
    data.agency.country = 'Brasil'

    if (data.agency.promoter_id === null || !data?.route?.id) {
      data.route = null
    }

    try {
      if (typeForm === 'create') {
        try {
          const dataCreate = {
            ...data,
            agency: {
              ...data.agency,
              promoter_id: data.agency.promoter_id || undefined
            }
          }
          activeLoading()
          await api.post(apiCreate(), dataCreate)
          disableLoading()
          updateDataTable()
          addToast({
            type: 'success',
            title: 'Registro criado',
            description: 'Registro criado com sucesso'
          })
          history.push(nameActions.read.to)
        } catch (error) {
          addToast({
            type: 'error',
            title: 'Erro ao adicionar o registro',
            description:
              'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
          })
          disableLoading()
          updateDataTable()
        }
      } else {
        const dataUpdate = {
          ...data
        }

        try {
          activeLoading()
          await api.put(apiUpdate(String(id)), dataUpdate)
          updateDataTable()
          disableLoading()
          history.push(nameActions.read.to)
          addToast({
            type: 'success',
            title: 'Registro atualizado',
            description: 'Registro alterado com sucesso'
          })
        } catch (error) {
          history.push(nameActions.read.to)
          addToast({
            type: 'error',
            title: 'Erro ao atualizar o registro',
            description:
              'Ocorreu um erro ao fazer a atualização, por favor, tente novamente.'
          })
        }
      }
      disableLoading()
    } catch (err) {
      if (typeForm === 'create') {
        addToast({
          type: 'error',
          title: 'Erro no cadastro',
          description:
            'Ocorreu um erro ao fazer cadastro, por favor, tente novamente.'
        })
        if (isOpenInModal) isOpenInModal.handleOnClose()
      }
    }
  }

  const loadPromoters = useCallback(async () => {
    try {
      const responsePromoters = await api.get(apiListPromotersOptions())
      setPromotersOptions(
        responsePromoters.data?.map(
          (item: { name: string; cpf: string; id: number }) => ({
            name: `${item.cpf} ${item.name}`,
            value: item.id
          })
        )
      )
    } catch (error) {}
  }, [])

  useEffect(() => {
    loadPromoters()
  }, [loadPromoters])

  const handleGetAddress = useCallback(async event => {
    const value = event.target.value
    setZipCodeValue(value)
    const zipCodeSearch = value?.replaceAll(/[.\-/]/g, '')
    if (zipCodeSearch.length === 8) {
      const addressResponse = await axios.get(
        `https://viacep.com.br/ws/${zipCodeSearch}/json`,
        {
          timeout: 10000
        }
      )
      if (addressResponse.data.erro) {
        return
      }
      setCityValue(addressResponse.data.localidade)
      setDistrictValue(addressResponse.data.bairro)
      setStreetValue(addressResponse.data.logradouro)
      setStateValue(addressResponse.data.uf)
    }
  }, [])

  const removeAgencyUser = (id: string | number) => {
    setAgencyUsers(prev =>
      prev.map(item => ({ ...item, show: item.id === id ? false : item.show }))
    )
  }

  const addAgencyUser = () => {
    setAgencyUsers(prev => [
      ...prev,
      {
        id: v4(),
        name: '',
        email: '',
        password: '',
        role: AGENCY_USER_ROLE_ENUM.ATTENDANT,
        show: true
      }
    ])
  }

  return (
    <Form onSubmit={onSubmitForm} defaultValues={defaultValues}>
      <>
        <div className="card mb-5 mb-xl-10">
          <FormContainer className="form">
            <h4>Dados da agência</h4>
            <div className="row mb-5">
              <SelectSearch
                name="agency.promoter_id"
                label="Promotor"
                className="col-md-4"
                options={[
                  {
                    name: 'Selecione um promotor',
                    value: null
                  },
                  ...promotersOptions
                ]}
                onSelectCallback={event => {
                  if (event?.value) {
                    fetchRoutesOptions(event.value.toString())
                  } else {
                    setRoutesOptions([])
                  }
                }}
                blank
                defaultValue=""
                controlled
                placeholder="Digite para buscar promotor..."
                noOptionsMessage="Nenhum promotor encontrado"
              />

              <Select
                className="col-md-4"
                name={'route.id'}
                label="Rota"
                options={routesOptions}
                blank
                controlled
              />
            </div>
            <div className="row mb-5">
              <Input
                name="agency.name"
                label="Nome da agência"
                rules={{ required: true }}
                className="col-md-4"
              />

              <Input
                name="agency.responsible"
                label="Responsável"
                className="col-md-4"
              />

              <Input
                name="agency.cnpj"
                label="CNPJ"
                className="col-md-4"
                mask={{
                  mask: INPUT_MASK.CNPJ
                }}
                controlled
              />
            </div>
            <div className="row mb-5">
              <Input
                name="agency.zip_code"
                label="Cep"
                className="col-md-2"
                mask={{
                  mask: INPUT_MASK.ZIP_CODE
                }}
                controlled
                onChange={handleGetAddress}
                value={zipCodeValue}
              />
              <Input
                name="agency.city"
                label="Cidade"
                className="col-md-3"
                controlled
                value={cityValue}
                onChange={event => {
                  setCityValue(event.target.value)
                }}
              />
              <Input
                name="agency.district"
                label="Bairro"
                className="col-md-3"
                value={districtValue}
                onChange={event => {
                  setDistrictValue(event.target.value)
                }}
                controlled
              />
              <Input
                name="agency.street"
                label="Endereço"
                className="col-md-3"
                value={streetValue}
                onChange={event => {
                  setStreetValue(event.target.value)
                }}
                controlled
              />

              <Input
                name="agency.number"
                label="Número"
                className="col-md-1"
                value={numberValue}
                onChange={event => {
                  setNumberValue(event.target.value)
                }}
                controlled
              />
              <Select
                name="agency.uf"
                label="Estado"
                className="col-md-2"
                options={SELECT_BRAZILIAN_STATES}
                value={stateValue}
                onChange={event => {
                  setStateValue(event.target.value)
                }}
                controlled
              />
              <Input
                name="agency.complement"
                label="Complemento"
                className="col-md-6"
              />
              <Input
                name="agency.whatsapp_number"
                label="Whatsapp"
                className="col-md-2"
                mask={{
                  mask: INPUT_MASK.CELL_PHONE
                }}
                controlled
              />
              <Input
                name="agency.phone_number"
                label="Telefone/Celular"
                className="col-md-2"
                mask={{
                  mask: [
                    { mask: INPUT_MASK.PHONE },
                    { mask: INPUT_MASK.CELL_PHONE }
                  ]
                }}
                controlled
              />
            </div>

            <div className="row mb-5">
              <Input
                name="agency.latitude"
                label="Latitude"
                className="col-md-2"
              />
              <Input
                name="agency.longitude"
                label="Longitude"
                className="col-md-2"
              />
              <Input
                name="agency.location_link_maps"
                label="Link da localização"
                className="col-md-4"
              />
              <Select
                className="col-md-4"
                name={'agency.partnership_type'}
                label="Tipo de parceria"
                options={Object.entries(AGENCY_PARTNERSHIP_TYPE_MAP).map(
                  ([key, label]) => ({
                    name: label,
                    value: key
                  })
                )}
                blank
              />
            </div>

            {typeForm === 'create' && (
              <>
                <h4 className="pt-4 pb-4">Usuários</h4>
                {agencyUsers.map(
                  (user, index) =>
                    user.show && (
                      <div className="row mb-5" key={user.id}>
                        <Input
                          name={`agencyUsers.${index}.name`}
                          label="Nome"
                          rules={{ required: true }}
                          className="col-md-3"
                          controlled
                        />
                        <Input
                          name={`agencyUsers.${index}.email`}
                          label="Email"
                          rules={{ required: true }}
                          className="col-md-3"
                          controlled
                        />
                        <Input
                          type="password"
                          name={`agencyUsers.${index}.password`}
                          label="Senha"
                          rules={{ required: typeForm === 'create' }}
                          className="col-md-3"
                          controlled
                        />
                        <Select
                          className="col-md-2"
                          name={`agencyUsers.${index}.role`}
                          label="Grupo de usuário"
                          options={Object.entries(AGENCY_USER_ROLE_MAP).map(
                            ([key, label]) => ({
                              name: label,
                              value: key
                            })
                          )}
                          blank
                          rules={{ required: true }}
                          defaultValue={user.role}
                          controlled
                        />
                        <div
                          className="col-md-1 d-flex align-items-end"
                          style={{ paddingBottom: '12px' }}
                        >
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => removeAgencyUser(user.id)}
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    )
                )}
                <div className="d-flex">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => addAgencyUser()}
                  >
                    Adicionar usuário
                  </button>
                </div>
              </>
            )}
          </FormContainer>
          <div className="card-footer d-flex justify-content-end py-6 px-9">
            <button type="submit" className="btn btn-primary">
              Salvar
            </button>
          </div>
        </div>
      </>
    </Form>
  )
}
